import Vue from 'vue'

import api from '@/api'
import DateService, { FMT, DayjsUnit } from '@/helpers/DateService'
import {
  addInterval,
  emergencyGuardStart,
  guardiasPreloadRange,
  intervalsOverlap,
  isBackendFallback,
  isCovered,
  localDayRange,
  MAX_WINDOW_MS,
  normalizeIntervals,
  overlapsAny,
  recursoSectorEstadoDates,
  removeInterval,
  rowSpan,
  splitByMaxSpan,
  subtractIntervals,
  toQuery,
  toValidDayjs,
} from '@/helpers/guardias/guardDateRanges'
import { buildGuardIndex, compareGuardPriority, findGuardForDateRecurso, findGuardsActiveOnDay } from '@/helpers/guardias/guardIndex'
import { normalizeId } from '@/helpers/IdHelper'
import { buildMedioPersonalByMedio } from '@/helpers/MedioPersonalHelper'
import { createSyncRequest, createSyncState, isProtected, recordAppliedMany, recordWrite, recordWriteMany, syncMutations } from '@/helpers/storeSync'
import i18n from '@/plugins/i18n.plugin'

const FULL_HISTORY_INTERVAL = Object.freeze({ from: -Infinity, to: Infinity })

const state = {
  medioPersonal: [],
  turnos: [],
  medioPersonalByMedio: {},
  loadedRanges: [],
  staleRanges: [],
  fullHistoryLoaded: false,
  pendingRequests: [],
  sync: createSyncState(['medioPersonal']),
  dayRolloverTimer: null,
  activeEmergenciaIds: {},
}

const getters = {
  medioPersonal: (state) => {
    return state.medioPersonal
  },
  turnos: (state) => {
    return state.turnos
  },

  turnosOperativos: (state) => (medioPersonal) => {
    return state.turnos.filter(turno => !turno.BORRADO || medioPersonal.ID_TURNO === turno.ID_TURNO)
  },

  guardIndex: (state) => {
    return buildGuardIndex(state.medioPersonal)
  },

  getMediosPersonalesByDate: (state, getters, rootState, rootGetters) => (date) => {
    const day = DateService.toDayjs(date)
    const isDynamic = (medioId) => Boolean(rootGetters['recurso/getMedioById'](medioId)?.MEDIO?.GUARDIAS_DINAMICAS)

    return findGuardsActiveOnDay(getters.guardIndex, day, isDynamic)
  },

  getMedioPersonalByDateRecurso: (state, getters) => (date, recurso) => {
    if (!date || !recurso) return null

    const dateFormatted = DateService.toDayjs(date)

    return findGuardForDateRecurso(getters.guardIndex, dateFormatted, recurso.MEDIO, recurso.PERSONAL)
  },

  isRangeLoaded: (state) => (from, to) => {
    return state.fullHistoryLoaded || isCovered({ from, to }, [...state.loadedRanges, ...state.staleRanges])
  },

  isActiveEmergencia: (state) => (idEmergencia) => Boolean(state.activeEmergenciaIds[normalizeId(idEmergencia)]),

  getMedioAsignado: (state) => (idPersonal, fecha) => {
    const t = DateService.toDayjs(fecha)

    return (
      state.medioPersonal.find(({ ID_PERSONAL, FECHA_ENTRADA, FECHA_SALIDA }) => {
        const start = DateService.toDayjs(FECHA_ENTRADA, FMT.dbTimestamp)
        if (!start.isValid()) return false

        const end = FECHA_SALIDA
          ? DateService.toDayjs(FECHA_SALIDA, FMT.dbTimestamp)
          : null

        // Comparación inclusiva en ambos extremos (equivalente a '[]' en moment.isBetween)
        const inRange =
          (t.isAfter(start) || t.isSame(start)) &&
          (end ? t.isBefore(end) || t.isSame(end) : true)

        return ID_PERSONAL === idPersonal && inRange
      }) || null
    )
  },

  getPersonalAsignado: (state) => (idMedio, fecha) => {
    const lista = state.medioPersonalByMedio?.[idMedio]
    if (!lista?.length) return null

    const t = DateService.toDayjs(fecha)

    const matches = lista.filter(({ FECHA_ENTRADA, FECHA_SALIDA }) => {
      const start = DateService.toDayjs(FECHA_ENTRADA, FMT.dbTimestamp)
      const end = FECHA_SALIDA ? DateService.toDayjs(FECHA_SALIDA, FMT.dbTimestamp) : null
      return (
        (t.isAfter(start) || t.isSame(start)) &&
        (end ? t.isBefore(end) || t.isSame(end) : true)
      )
    })

    if (!matches.length) return null

    const medioId = normalizeId(idMedio)
    return matches.reduce((best, current) =>
      compareGuardPriority(current, best, medioId, null) < 0 ? current : best
    )
  },

}

const mutations = {
  ...syncMutations,

  SOCKET_addMedioPersonal(state, mediosPersonales) {
    const acceptedRanges = [
      ...state.loadedRanges,
      ...state.staleRanges,
      ...state.pendingRequests.map((entry) => entry.interval),
    ]
    const rowsInRange = state.fullHistoryLoaded
      ? mediosPersonales
      : mediosPersonales.filter((row) => overlapsAny(rowSpan(row), acceptedRanges))

    if (!rowsInRange.length) return

    state.medioPersonal = state.medioPersonal
      .filter(x =>
        x.NUMERO_COMPONENTES ||
        rowsInRange.some(y => y.ID_MEDIO_PERSONAL === x.ID_MEDIO_PERSONAL) ||
        !rowsInRange.some(y => y.FECHA === x.FECHA &&
          (y.ID_MEDIO === x.ID_MEDIO || y.ID_PERSONAL === x.ID_PERSONAL)))

    const nextRows = state.medioPersonal.slice()
    const idIndex = new Map()
    nextRows.forEach((row, index) => {
      const id = normalizeId(row.ID_MEDIO_PERSONAL)
      if (id !== null && !idIndex.has(id)) idIndex.set(id, index)
    })

    let creadas = 0
    let actualizadas = 0

    rowsInRange.forEach(element => {
      const id = normalizeId(element.ID_MEDIO_PERSONAL)
      const indexFound = id !== null ? idIndex.get(id) : undefined

      if (indexFound !== undefined) {
        actualizadas++
        nextRows[indexFound] = element
      } else {
        nextRows.push(element)
        if (id !== null) idIndex.set(id, nextRows.length - 1)
        creadas++
      }
    })

    state.medioPersonal = nextRows
    recordWriteMany(state.sync, 'medioPersonal', rowsInRange.map(row => row.ID_MEDIO_PERSONAL))

    state.medioPersonalByMedio = buildMedioPersonalByMedio(state.medioPersonal)
    this.notify(i18n.t('alert.guardsUpdated'), i18n.t('alert.guardsUpdate', { creadas: creadas, actualizadas: actualizadas }))
  },

  SOCKET_removeMedioPersonal(state, idMedioPersonal) {
    const id = normalizeId(idMedioPersonal)
    recordWrite(state.sync, 'medioPersonal', id, true)

    const index = state.medioPersonal.findIndex(x => normalizeId(x.ID_MEDIO_PERSONAL) === id)

    if (index !== -1) {
      state.medioPersonal.splice(index, 1)
      state.medioPersonalByMedio = buildMedioPersonalByMedio(state.medioPersonal)
      this.notify(i18n.t('alert.guardDeleted'), i18n.t('alert.guardDelete'))
    }
  },

  ADD_PENDING_REQUEST(state, entry) {
    state.pendingRequests.push(entry)
  },

  REMOVE_PENDING_REQUEST(state, entry) {
    state.pendingRequests = state.pendingRequests.filter((e) => e !== entry)
  },

  SET_DAY_ROLLOVER_TIMER(state, handle) {
    state.dayRolloverTimer = handle
  },

  SET_ACTIVE_EMERGENCIAS(state, ids) {
    state.activeEmergenciaIds = Object.fromEntries(ids.map((id) => [id, true]))
  },

  TRACK_ACTIVE_EMERGENCIA(state, idEmergencia) {
    const id = normalizeId(idEmergencia)
    if (id !== null) Vue.set(state.activeEmergenciaIds, id, true)
  },

  UNTRACK_ACTIVE_EMERGENCIA(state, idEmergencia) {
    Vue.delete(state.activeEmergenciaIds, normalizeId(idEmergencia))
  },

  MERGE_GUARDIAS(state, { rows, interval, request, replace }) {
    const nextRows = state.medioPersonal.slice()
    const idIndex = new Map()
    nextRows.forEach((row, index) => {
      const id = normalizeId(row.ID_MEDIO_PERSONAL)
      if (id !== null) idIndex.set(id, index)
    })

    const appliedIds = new Set()

    rows.forEach((row) => {
      const id = normalizeId(row.ID_MEDIO_PERSONAL)
      const stamp = id !== null ? state.sync.stamps.medioPersonal[id] : undefined
      if (isProtected(stamp, request)) return

      const existingIndex = id !== null ? idIndex.get(id) : undefined
      if (existingIndex !== undefined) {
        nextRows[existingIndex] = row
      } else {
        nextRows.push(row)
        if (id !== null) idIndex.set(id, nextRows.length - 1)
      }

      if (id !== null) appliedIds.add(id)
    })

    if (replace) {
      state.medioPersonal = nextRows.filter((row) => {
        const id = normalizeId(row.ID_MEDIO_PERSONAL)
        if (id !== null && appliedIds.has(id)) return true
        if (!overlapsAny(rowSpan(row), [interval])) return true

        const stamp = id !== null ? state.sync.stamps.medioPersonal[id] : undefined
        return isProtected(stamp, request)
      })
    } else {
      state.medioPersonal = nextRows
    }

    recordAppliedMany(state.sync, 'medioPersonal', appliedIds, request.seq)
    state.medioPersonalByMedio = buildMedioPersonalByMedio(state.medioPersonal)
  },

  MARK_RANGE_LOADED(state, interval) {
    state.loadedRanges = addInterval(state.loadedRanges, interval)
    state.staleRanges = removeInterval(state.staleRanges, interval)
  },

  MARK_RANGE_FAILED(state, { interval, wasLoaded }) {
    if (!wasLoaded) return

    state.loadedRanges = removeInterval(state.loadedRanges, interval)
    state.staleRanges = addInterval(state.staleRanges, interval)
  },

  SET_FULL_HISTORY(state) {
    state.fullHistoryLoaded = true
  },

  SOCKET_addTurno(state, turno) {
    state.turnos.push(turno)
    this.notify(i18n.t('alert.shiftCreated'), i18n.t('alert.shiftCreate'))
  },

  SOCKET_updateTurno(state, turnoActualizado) {
    const index = state.turnos.findIndex(t => t.ID_TURNO === turnoActualizado.ID_TURNO)

    if (index !== -1) {
      if (turnoActualizado.BORRADO) {
        state.turnos.splice(index, 1)
      } else {
        Vue.set(state.turnos, index, turnoActualizado)
      }

      this.notify(
        i18n.t('alert.shiftUpdated'),
        i18n.t('alert.shiftUpdate', { turno: turnoActualizado.NOMBRE })
      )
    }
  },

  SET_TURNOS(state, turnos) {
    state.turnos = turnos
  },

  SET_TURNO(state, turno) {
    state.turnos.push(turno)
    this.notify(i18n.t('alert.shiftCreated'), i18n.t('alert.shiftCreateSuccess', { date: DateService.toDayjs(turno.FECHA).format(FMT.onlyDate) }))
  },

  UPDATE_TURNO(state, turnoActualizado) {
    const index = state.turnos.findIndex(t => t.ID_TURNO === turnoActualizado.ID_TURNO)
    if (index !== -1) {
      Vue.set(state.turnos, index, { ...state.turnos[index], ...turnoActualizado })
      this.notify(i18n.t('alert.shiftUpdated'), i18n.t('alert.shiftUpdated', { date: DateService.toDayjs(turnoActualizado.FECHA).format(FMT.onlyDate) }))
    }
  }
}

const actions = {
  async requestGuardiaChunk({ state, dispatch }, { chunk, replace }) {
    const eligible = state.pendingRequests.filter(
      (entry) => (!replace || entry.replace) && intervalsOverlap(entry.interval, chunk)
    )

    const containing = eligible.find((entry) => isCovered(chunk, [entry.interval]))
    if (containing) return containing.promise

    const remainder = subtractIntervals(chunk, eligible.map((entry) => entry.interval))
    const newPromises = remainder.map((interval) => dispatch('fetchGuardiaRange', { interval, replace }))
    const results = await Promise.all([...eligible.map((entry) => entry.promise), ...newPromises])

    return results.every(Boolean)
  },

  requestGuardiaChunks({ dispatch }, { chunks, replace }) {
    return Promise.all(chunks.map((chunk) => dispatch('requestGuardiaChunk', { chunk, replace })))
  },

  async ensureGuardias({ state, dispatch }, { from, to }) {
    if (state.fullHistoryLoaded) return true

    const missing = subtractIntervals({ from, to }, state.loadedRanges)
    if (!missing.length) return true

    const freshChunks = []
    const staleChunks = []

    missing.forEach((piece) => {
      const freshParts = subtractIntervals(piece, state.staleRanges)
      const staleParts = subtractIntervals(piece, freshParts)

      freshChunks.push(...freshParts.flatMap((part) => splitByMaxSpan(part, MAX_WINDOW_MS)))
      staleChunks.push(...staleParts.flatMap((part) => splitByMaxSpan(part, MAX_WINDOW_MS)))
    })

    const [freshResults, staleResults] = await Promise.all([
      dispatch('requestGuardiaChunks', { chunks: freshChunks, replace: false }),
      dispatch('requestGuardiaChunks', { chunks: staleChunks, replace: true }),
    ])

    return [...freshResults, ...staleResults].every(Boolean)
  },

  async reloadGuardias({ dispatch }, { from, to }) {
    const chunks = splitByMaxSpan({ from, to }, MAX_WINDOW_MS)
    const results = await dispatch('requestGuardiaChunks', { chunks, replace: true })

    return results.every(Boolean)
  },

  async reloadLoadedGuardias({ state, commit, dispatch }) {
    if (state.fullHistoryLoaded) {
      commit('BEGIN_SYNC_REQUEST')
      const request = createSyncRequest(state.sync.seq)
      try {
        const response = await api.recurso.getMedioPersonal()
        commit('MERGE_GUARDIAS', { rows: response.data, interval: FULL_HISTORY_INTERVAL, request, replace: true })
        return true
      } catch (err) {
        if (err?.response?.status === 400) console.error(err.response.data)
        this.notify(i18n.t('alert.error.error'), i18n.t('alert.error.guardiasLoad'), 'error')
        return false
      }
    }

    const ranges = normalizeIntervals([...state.loadedRanges, ...state.staleRanges])
    if (!ranges.length) return true

    const results = await Promise.all(ranges.map((range) => dispatch('reloadGuardias', range)))
    return results.every(Boolean)
  },

  async ensureBaseGuardias({ dispatch }) {
    const today = DateService.getCurrentDayJs()
    return dispatch('ensureGuardias', localDayRange(today.subtract(1, DayjsUnit.Day), today.add(1, DayjsUnit.Day)))
  },

  async ensureGuardiasForEmergencias({ commit, dispatch }, emergencias) {
    const active = (emergencias || []).filter((emergencia) => emergencia && !emergencia.ELIMINADO)

    commit(
      'SET_ACTIVE_EMERGENCIAS',
      active.map((emergencia) => normalizeId(emergencia.ID_EMERGENCIA)).filter((id) => id !== null)
    )

    const starts = active
      .map((emergencia) => emergencyGuardStart(emergencia))
      .filter((ms) => ms !== null)
    if (!starts.length) return true

    const start = DateService.toDayjs(Math.min(...starts))
    const today = DateService.getCurrentDayJs()
    return dispatch('ensureGuardias', localDayRange(start, today.add(1, DayjsUnit.Day)))
  },

  async ensureGuardiasForDates({ dispatch }, dates) {
    const validDays = (dates || []).map(toValidDayjs).filter((day) => day !== null)
    if (!validDays.length) return true

    const earliest = validDays.reduce((min, day) => (day.isBefore(min) ? day : min))
    const today = DateService.getCurrentDayJs()
    return dispatch('ensureGuardias', localDayRange(earliest, today.add(1, DayjsUnit.Day)))
  },

  untrackActiveEmergencia({ commit }, idEmergencia) {
    commit('UNTRACK_ACTIVE_EMERGENCIA', idEmergencia)
  },

  trackAndLoadEmergencia({ commit, dispatch }, emergencia) {
    commit('TRACK_ACTIVE_EMERGENCIA', emergencia?.ID_EMERGENCIA)
    const start = emergencyGuardStart(emergencia || {})
    return dispatch('ensureGuardiasForDates', start === null ? [] : [start])
  },

  trackUnhiddenEmergencia({ dispatch }, payload) {
    if (payload?.OCULTO !== false) return
    return dispatch('trackAndLoadEmergencia', payload.EMERGENCIA)
  },

  loadEditedEmergenciaStart({ dispatch, getters }, payload) {
    if (payload?.CODIGO_TIPO_ESTADO !== 0) return
    if (!getters.isActiveEmergencia(payload?.ID_EMERGENCIA)) return
    return dispatch('ensureGuardiasForDates', [payload.FECHA])
  },

  loadAddedRecursosDates({ dispatch }, payload) {
    return dispatch('ensureGuardiasForDates', recursoSectorEstadoDates(payload))
  },

  loadEditedRecursoDates({ dispatch }, payload) {
    return dispatch('ensureGuardiasForDates', recursoSectorEstadoDates([payload]))
  },

  startDayRollover({ state, commit, dispatch }) {
    if (state.dayRolloverTimer !== null) clearTimeout(state.dayRolloverTimer)

    const now = DateService.getCurrentDayJs()
    const delay = now.add(1, DayjsUnit.Day).startOf(DayjsUnit.Day).add(1, DayjsUnit.Second).diff(now)

    const handle = setTimeout(async () => {
      await dispatch('ensureBaseGuardias')
      dispatch('startDayRollover')
    }, delay)
    commit('SET_DAY_ROLLOVER_TIMER', handle)
  },

  async fetchGuardiaRange({ commit, state, dispatch }, { interval, replace }) {
    commit('BEGIN_SYNC_REQUEST')
    const request = createSyncRequest(state.sync.seq)

    const promise = dispatch('loadGuardiaRange', { interval, replace, request })
    const entry = Object.freeze({ interval, replace, promise })
    commit('ADD_PENDING_REQUEST', entry)

    try {
      return await promise
    } finally {
      commit('REMOVE_PENDING_REQUEST', entry)
    }
  },

  async loadGuardiaRange({ commit, state }, { interval, replace, request }) {
    try {
      const response = await api.recurso.getMedioPersonal(toQuery(interval))
      const rows = response.data

      if (isBackendFallback(rows, interval)) {
        commit('SET_FULL_HISTORY')
      }

      commit('MERGE_GUARDIAS', { rows, interval, request, replace })
      commit('MARK_RANGE_LOADED', interval)
      return true
    } catch (err) {
      if (err?.response?.status === 400) console.error(err.response.data)
      this.notify(i18n.t('alert.error.error'), i18n.t('alert.error.guardiasLoad'), 'error')
      const wasLoaded = isCovered(interval, state.loadedRanges)
      commit('MARK_RANGE_FAILED', { interval, wasLoaded })
      return false
    }
  },

  async fetchTurnos({ commit }) {


    try {
      const response = await api.recurso.getTurnos()
      commit('SET_TURNOS', response.data)

    } catch (err) {
      console.error(err)
      throw err
    }
  },

  // data: array expected
  async addMedioPersonal({ _commit }, data) {

    try {
      await api.recurso.setMedioPersonal(data)


    } catch (err) {
      this.notify(i18n.t('alert.error.personalRscAdd'), i18n.t('alert.error.personalRscAdd'), 'error')
      console.error(err)
      throw err
    }
  },

  async removeMedioPersonal({ _commit }, data) {

    try {
      await api.recurso.deleteMedioPersonal(data)
    } catch (err) {
      this.notify(i18n.t('alert.error.personalRscDelete'), i18n.t('alert.error.personalRscAdd'), 'error')
      console.error(err)
      throw err
    }
  },

  async addTurno({ commit }, data) {
    try {
      await api.recurso.setTurno(data)
    } catch (err) {
      console.error(err)
      throw err
    }
  },

  async updateTurno({ commit }, data) {
    try {
      await api.recurso.updateTurno(data)
    } catch (err) {
      console.error(err)
      throw err
    }
  },

  async addGuardias({ dispatch, getters, rootGetters }, datosGuardias) {
    if (datosGuardias.length) {
      const preloaded = await dispatch('ensureGuardias', guardiasPreloadRange(datosGuardias))
      if (!preloaded) {
        this.notify(i18n.t('alert.error.error'), i18n.t('alert.error.addGuardError'), 'error')
        throw new Error(i18n.t('alert.error.addGuardError'))
      }
    }

    const medioPersonales = []

    for (let datoGuardia of datosGuardias) {
      let { procedencia, idTurno, idMedio, idPersonal, numComponentes } = datoGuardia

      const init = DateService.toDayjs(datoGuardia.init, FMT.ISODate)
      const end = DateService.toDayjs(datoGuardia.end, FMT.ISODate)

      const numDiasExtender = end.diff(init, 'days')

      const turnoSelected = getters.turnos.find(x => x.ID_TURNO === idTurno)
      if (!turnoSelected) return
      for (let i = 0; i < numDiasExtender; i++) {
        const date = DateService.toDayjs(init, FMT.onlyDate).add(i, 'day')

        const fechaEntrada = DateService.toDayjs(`${date.format(FMT.onlyDate)} ${turnoSelected.HORA_ENTRADA}`, FMT.date)
        const fechaSalida = DateService.toDayjs(`${date.format(FMT.onlyDate)} ${turnoSelected.HORA_SALIDA}`, FMT.date)

        const recurso = procedencia === 'medio' ? rootGetters['recurso/getMedioById'](idMedio) : rootGetters['recurso/getPersonalById'](idPersonal)
        const ID_MEDIO_PERSONAL = getters.getMedioPersonalByDateRecurso(date, recurso)?.ID_MEDIO_PERSONAL

        const medioPersonal = {
          ID_MEDIO_PERSONAL: ID_MEDIO_PERSONAL?.toUpperCase(),
          ID_PERSONAL: idPersonal,
          ID_MEDIO: idMedio,
          ID_TURNO: idTurno,
          FECHA: date,
          FECHA_ENTRADA: fechaEntrada,
          FECHA_SALIDA: turnoSelected.HORA_SALIDA <= turnoSelected.HORA_ENTRADA ? fechaSalida.add(1, 'days') : fechaSalida,
          BORRADO: 0,
          NUMERO_COMPONENTES: numComponentes
        }

        medioPersonales.push(medioPersonal)
      }
    }

    try {
      await dispatch('addMedioPersonal', medioPersonales)
    } catch (error) {
      console.error(i18n.t('alert.error.addGuards'), error)
      throw error
    }
  }

}

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export default module
